import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const partnersByCategory = {
  formation: [
    { name: "Université Paris-Sorbonne", logo: "https://via.placeholder.com/150?text=Paris-Sorbonne", description: "Partenariat académique pour les programmes d'échange" },
    { name: "McGill University", logo: "https://via.placeholder.com/150?text=McGill", description: "Coopération en recherche et développement" },
    { name: "Université de Genève", logo: "https://via.placeholder.com/150?text=Geneva", description: "Programmes conjoints en droit international" }
  ],
  media: [
    { name: "RTI", logo: "https://via.placeholder.com/150?text=RTI", description: "Couverture médiatique des événements" },
    { name: "Fraternité Matin", logo: "https://via.placeholder.com/150?text=Fraternite", description: "Publications et communications" },
    { name: "Africa News", logo: "https://via.placeholder.com/150?text=AfricaNews", description: "Diffusion internationale" }
  ],
  stage: [
    { name: "Orange Côte d'Ivoire", logo: "https://via.placeholder.com/150?text=Orange", description: "Opportunités de stage en technologie" },
    { name: "Société Générale CI", logo: "https://via.placeholder.com/150?text=SG", description: "Stages en finance et banque" },
    { name: "Deloitte Afrique", logo: "https://via.placeholder.com/150?text=Deloitte", description: "Stages en audit et conseil" }
  ],
  institution: [
    { name: "Ministère de l'Enseignement Supérieur", logo: "https://via.placeholder.com/150?text=MESRS", description: "Reconnaissance et accréditation" },
    { name: "UNESCO", logo: "https://via.placeholder.com/150?text=UNESCO", description: "Programmes de développement éducatif" },
    { name: "Banque Mondiale", logo: "https://via.placeholder.com/150?text=WB", description: "Financement de projets éducatifs" }
  ]
};

const categories = [
  { id: 'tous', label: 'Tous les Partenaires' },
  { id: 'formation', label: 'Formation' },
  { id: 'media', label: 'Médias' },
  { id: 'stage', label: 'Stages & Emplois' },
  { id: 'institution', label: 'Institutions' }
];

export default function Partenaires() {
  const [activeCategory, setActiveCategory] = useState('tous');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    entreprise: '',
    contact_nom: '',
    email: '',
    telephone: '',
    type_partenariat: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.entreprise || !formData.contact_nom || !formData.email || !formData.telephone || !formData.type_partenariat || !formData.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/partner-requests`, formData);
      
      if (response.status === 200) {
        toast.success('Demande envoyée avec succès! Nous vous contacterons bientôt.');
        setFormData({
          entreprise: '',
          contact_nom: '',
          email: '',
          telephone: '',
          type_partenariat: '',
          message: ''
        });
        setShowForm(false);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const getAllPartners = () => {
    return Object.values(partnersByCategory).flat();
  };

  const getPartnersByCategory = (category) => {
    if (category === 'tous') return getAllPartners();
    return partnersByCategory[category] || [];
  };

  return (
    <div className="min-h-screen bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-brand-secondary">Nos Partenaires</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Ensemble, nous construisons l'avenir de l'éducation supérieure
          </p>
        </div>

        {/* Category Tabs */}
        <Tabs defaultValue="tous" className="w-full mb-12">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 bg-gray-100 p-2 rounded-lg gap-2">
            {categories.map(category => (
              <TabsTrigger 
                key={category.id}
                value={category.id}
                data-testid={`tab-${category.id}`}
                onClick={() => setActiveCategory(category.id)}
                className="data-[state=active]:bg-brand-primary data-[state=active]:text-white font-semibold py-2 px-4 rounded-md transition-all"
              >
                {category.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {categories.map(category => (
            <TabsContent key={category.id} value={category.id} className="mt-8">
              <div className="grid md:grid-cols-3 gap-8">
                {getPartnersByCategory(category.id).map((partner, index) => (
                  <Card 
                    key={index} 
                    className="p-6 card-hover bg-white border-gray-200 shadow-md text-center"
                    data-testid={`partner-card-${index}`}
                  >
                    <div className="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                      <img src={partner.logo} alt={partner.name} className="w-full h-full object-contain" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-secondary mb-2">{partner.name}</h3>
                    <p className="text-gray-600 text-sm">{partner.description}</p>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white p-12 rounded-lg shadow-xl text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Devenez notre partenaire</h2>
          <p className="text-lg mb-6">
            Rejoignez notre réseau de partenaires et contribuez à l'excellence éducative
          </p>
          <Button 
            data-testid="become-partner-btn"
            onClick={() => setShowForm(!showForm)}
            className="bg-white text-brand-primary hover:bg-brand-accent font-bold px-8 py-4 rounded-md shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
          >
            {showForm ? 'Masquer le formulaire' : 'Devenir Partenaire'}
          </Button>
        </div>

        {/* Partnership Form */}
        {showForm && (
          <Card className="p-8 md:p-12 shadow-xl bg-white max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-brand-primary text-center">Formulaire de Partenariat</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="entreprise" className="text-brand-secondary font-semibold mb-2 block">Nom de l'Entreprise/Institution *</Label>
                  <Input
                    id="entreprise"
                    name="entreprise"
                    data-testid="input-entreprise"
                    value={formData.entreprise}
                    onChange={handleChange}
                    placeholder="Nom de votre organisation"
                    className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="contact_nom" className="text-brand-secondary font-semibold mb-2 block">Nom du Contact *</Label>
                  <Input
                    id="contact_nom"
                    name="contact_nom"
                    data-testid="input-contact-nom"
                    value={formData.contact_nom}
                    onChange={handleChange}
                    placeholder="Votre nom complet"
                    className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-brand-secondary font-semibold mb-2 block">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre.email@example.com"
                    className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="telephone" className="text-brand-secondary font-semibold mb-2 block">Téléphone *</Label>
                  <Input
                    id="telephone"
                    name="telephone"
                    data-testid="input-telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="Numéro de téléphone"
                    className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="type_partenariat" className="text-brand-secondary font-semibold mb-2 block">Type de Partenariat *</Label>
                <Select 
                  value={formData.type_partenariat} 
                  onValueChange={(value) => handleSelectChange('type_partenariat', value)}
                >
                  <SelectTrigger data-testid="select-type-partenariat" className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary">
                    <SelectValue placeholder="Choisissez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="formation">Formation & Éducation</SelectItem>
                    <SelectItem value="media">Médias & Communication</SelectItem>
                    <SelectItem value="stage">Stages & Emplois</SelectItem>
                    <SelectItem value="institution">Partenariat Institutionnel</SelectItem>
                    <SelectItem value="recherche">Recherche & Développement</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-brand-secondary font-semibold mb-2 block">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="textarea-message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Décrivez votre projet de partenariat..."
                  rows={6}
                  className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary resize-none"
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="submit-partner-request-btn"
                disabled={loading}
                className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-6 text-lg font-semibold rounded-md transition-all hover:shadow-lg hover:-translate-y-1"
              >
                {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}